"use client"

import { useEffect } from "react"
import { AlertTriangle, RefreshCw, Mail } from "lucide-react"
import Link from "next/link"
import { SocialLinks } from "@/components/social-links"

export default function ErrorPage({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  useEffect(() => {
    // Log the error to an error reporting service
    console.error("App error:", error)
  }, [error])

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="mb-6 flex justify-center">
          <div className="p-4 bg-red-100 dark:bg-red-900/20 rounded-full">
            <AlertTriangle className="h-12 w-12 text-red-500 dark:text-red-400" />
          </div>
        </div>

        <h1 className="text-3xl font-bold mb-4">Something went wrong</h1>

        <p className="text-gray-600 dark:text-gray-400 mb-8">
          We're sorry, but we encountered an unexpected error. Our team has been notified.
        </p>

        <div className="space-y-4">
          <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-xl">
            <h2 className="font-bold mb-2">What you can do:</h2>
            <ul className="text-left text-sm space-y-2">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <button onClick={reset} className="text-[#8E82FF] hover:underline flex items-center">
                  <RefreshCw className="h-4 w-4 mr-1" /> Try again
                </button>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <span>Clear your browser cache and cookies</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                <Link href="/" className="text-[#8E82FF] hover:underline">
                  Return to the home page
                </Link>
              </li>
            </ul>
          </div>

          <div className="p-4 bg-[rgba(142,130,255,0.1)] rounded-xl">
            <h2 className="font-bold mb-2">Need help?</h2>
            <div className="flex flex-col sm:flex-row gap-3 mt-3 justify-center items-center">
              <a
                href="mailto:support@thehiitpit.com"
                className="flex items-center justify-center gap-2 px-2 py-2 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <Mail className="h-4 w-4" />
              </a>
              <div className="flex">
                <SocialLinks linkedin bsky reddit />
              </div>
            </div>
          </div>

          <button
            onClick={() => window.location.reload()}
            className="block w-full px-6 py-3 rounded-full bg-[#8E82FF] text-white font-medium hover:bg-opacity-90 transition-colors text-center"
          >
            Refresh the page
          </button>
        </div>

        {/* Error details (only in development) */}
        {process.env.NODE_ENV === "development" && (
          <div className="mt-8 p-4 bg-gray-100 dark:bg-gray-800 rounded-xl text-left overflow-auto max-h-64">
            <h3 className="font-bold mb-2 text-red-500">Error Details (Development Only):</h3>
            <p className="text-sm font-mono mb-2">{error.message}</p>
            <details>
              <summary className="text-sm cursor-pointer">Stack trace</summary>
              <pre className="text-xs mt-2 whitespace-pre-wrap">{error.stack || "No stack trace available"}</pre>
            </details>
          </div>
        )}
      </div>
    </div>
  )
}
